import { useId } from "react";
import { ModalShell } from "../../primitives/index.js";
import { RailPanelHeader } from "../RailPanel/RailPanelHeader.js";

export interface RightDrawerProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  badges?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  width?: number;
}

/**
 * Right-side overlay drawer. Engine-backed since increment 5.3: ModalShell
 * provides the portal, scrim, focus trap (with return focus), Esc/outside
 * dismissal, scroll lock, and inert background — the hand-rolled trap and
 * raw z-40 are retired. Used by Hoshin Monitor's X-Matrix item, Decision
 * detail, and other right-pane detail flows.
 *
 * The header is the shared RailPanelHeader (OK-28, PR 3): `subtitle` is its
 * eyebrow, `badges` sit under the title, and the close is its ghost Button.
 */
export function RightDrawer({
  open,
  onClose,
  title,
  subtitle,
  badges,
  footer,
  children,
  width = 460,
}: RightDrawerProps) {
  const titleId = useId();

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      containerClassName="justify-end"
      motionPreset="right-sheet"
      panelClassName="h-full bg-pb-card border-s border-pb-border flex flex-col shadow-xl"
      panelStyle={{ width, maxWidth: "100vw" }}
    >
      <RailPanelHeader
        title={title}
        eyebrow={subtitle}
        titleId={titleId}
        onClose={onClose}
        closeLabel="Close panel"
      >
        {badges}
      </RailPanelHeader>
      <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
      {footer && (
        <footer className="px-5 py-3 border-t border-pb-border bg-pb-card-elevated flex items-center gap-2">
          {footer}
        </footer>
      )}
    </ModalShell>
  );
}
